import { useDeferredValue, useEffect, useMemo, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  formatFleetMasterTimestamp,
  useFleetMasterDataWorkspaceContext,
} from "@/features/operator/fleet/fleet-master-data-shared"
import {
  type FleetOnboardingSearch,
  FLEET_VERTICAL_PRIMARY_OPERATOR_LABELS,
  FLEET_VERTICAL_SECONDARY_OPERATOR_LABELS,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  type FleetOperatingUnitRecord,
  type FleetPersonRecord,
  isApiError,
} from "@/lib/api/client"

interface FleetPeoplePageProps {
  search?: FleetOnboardingSearch
}

interface FleetPersonFormState {
  fullName: string
  operatingUnitId: string
  phone: string
  roleType: string
  status: string
}

const DEFAULT_FORM_STATE: FleetPersonFormState = {
  fullName: "",
  operatingUnitId: "",
  phone: "",
  roleType: "driver",
  status: "active",
}

function toFormState(record?: FleetPersonRecord | null): FleetPersonFormState {
  if (!record) {
    return DEFAULT_FORM_STATE
  }

  return {
    fullName: record.fullName,
    operatingUnitId: record.operatingUnitId || "",
    phone: record.phone || "",
    roleType: record.roleType,
    status: record.status,
  }
}

export function FleetPeoplePage({ search }: FleetPeoplePageProps) {
  const { organizationId, organizationLabel, sessionError, sessionStatus, vertical } =
    useFleetMasterDataWorkspaceContext(search)
  const [people, setPeople] = useState<FleetPersonRecord[]>([])
  const [units, setUnits] = useState<FleetOperatingUnitRecord[]>([])
  const [formState, setFormState] = useState<FleetPersonFormState>({
    ...DEFAULT_FORM_STATE,
    roleType:
      vertical === "two_wheeler_delivery"
        ? "rider"
        : vertical === "intercity_bus"
          ? "driver"
          : "driver",
  })
  const [editingPersonId, setEditingPersonId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeletingPersonId, setIsDeletingPersonId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState("")
  const deferredSearchText = useDeferredValue(searchText)
  const primaryOperatorLabel = FLEET_VERTICAL_PRIMARY_OPERATOR_LABELS[vertical]
  const secondaryOperatorLabel = FLEET_VERTICAL_SECONDARY_OPERATOR_LABELS[vertical]

  useEffect(() => {
    setFormState((previous) => ({
      ...previous,
      roleType:
        vertical === "two_wheeler_delivery"
          ? "rider"
          : vertical === "intercity_bus"
            ? "driver"
            : "driver",
    }))
  }, [vertical])

  useEffect(() => {
    if (!organizationId) {
      setPeople([])
      setUnits([])
      return
    }

    let isActive = true
    setIsLoading(true)
    setError(null)

    void Promise.all([
      apiClient.getFleetPeople({ organizationId, vertical }),
      apiClient.getFleetOperatingUnits({ organizationId }),
    ])
      .then(([personRecords, unitRecords]) => {
        if (!isActive) {
          return
        }

        setPeople(personRecords)
        setUnits(unitRecords)
      })
      .catch((loadError) => {
        if (!isActive) {
          return
        }

        setError(
          isApiError(loadError)
            ? loadError.message
            : loadError instanceof Error
              ? loadError.message
              : "Failed to load fleet people."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [organizationId, vertical])

  const filteredPeople = useMemo(() => {
    const query = deferredSearchText.trim().toLowerCase()
    if (!query) {
      return people
    }

    return people.filter((person) =>
      [
        person.fullName,
        person.roleType,
        person.phone || "",
        person.status,
        person.operatingUnit?.code || "",
        person.operatingUnit?.name || "",
      ].some((value) => value.toLowerCase().includes(query))
    )
  }, [deferredSearchText, people])

  const summary = useMemo(() => {
    return {
      activePeople: people.filter((person) => person.status === "active").length,
      assignedPeople: people.filter((person) => person.assignmentCount > 0).length,
      primaryRoles: people.filter((person) =>
        ["driver", "rider"].includes(person.roleType)
      ).length,
      uniqueRoles: new Set(people.map((person) => person.roleType)).size,
    }
  }, [people])

  const unitOptions = useMemo(() => {
    return units.filter((unit) => unit.vertical === vertical).length > 0
      ? units.filter((unit) => unit.vertical === vertical)
      : units
  }, [units, vertical])

  function resetForm() {
    setEditingPersonId(null)
    setFormState({
      ...DEFAULT_FORM_STATE,
      roleType:
        vertical === "two_wheeler_delivery"
          ? "rider"
          : vertical === "intercity_bus"
            ? "driver"
            : "driver",
    })
  }

  function beginEditing(person: FleetPersonRecord) {
    setEditingPersonId(person.id)
    setFormState(toFormState(person))
    setNotice(null)
    setError(null)
  }

  async function savePerson() {
    if (!organizationId) {
      setError("No Fleet organization is selected for people management.")
      return
    }

    setIsSaving(true)
    setNotice(null)
    setError(null)

    try {
      const payload = {
        ...formState,
        organizationId,
        operatingUnitId: formState.operatingUnitId || null,
        vertical,
      }

      const savedPerson = editingPersonId
        ? await apiClient.updateFleetPerson(editingPersonId, payload)
        : await apiClient.createFleetPerson(payload)

      setPeople((previous) =>
        editingPersonId
          ? previous.map((person) => (person.id === savedPerson.id ? savedPerson : person))
          : [savedPerson, ...previous]
      )
      setNotice(
        editingPersonId
          ? `Updated person ${savedPerson.fullName}.`
          : `Created person ${savedPerson.fullName}.`
      )
      resetForm()
    } catch (saveError) {
      setError(
        isApiError(saveError)
          ? saveError.message
          : saveError instanceof Error
            ? saveError.message
            : "Failed to save the fleet person."
      )
    } finally {
      setIsSaving(false)
    }
  }

  async function deletePerson(person: FleetPersonRecord) {
    if (!organizationId) {
      return
    }

    setIsDeletingPersonId(person.id)
    setNotice(null)
    setError(null)

    try {
      await apiClient.deleteFleetPerson(person.id, organizationId)
      setPeople((previous) => previous.filter((record) => record.id !== person.id))
      if (editingPersonId === person.id) {
        resetForm()
      }
      setNotice(`Deleted person ${person.fullName}.`)
    } catch (deleteError) {
      setError(
        isApiError(deleteError)
          ? deleteError.message
          : deleteError instanceof Error
            ? deleteError.message
            : "Failed to delete the fleet person."
      )
    } finally {
      setIsDeletingPersonId(null)
    }
  }

  return (
    <FleetB2BPageShell
      activeSection="people"
      title="Fleet people"
      description="Manage drivers, helpers, conductors, riders, supervisors, and other people records separately from asset data so assignment history stays reliable."
      highlights={[
        "People are persisted as first-class records with role, phone, and operating-unit scope.",
        "Driver and rider churn updates assignments instead of overwriting asset identity.",
        "This page is now the starting point for workforce-linked safety analytics and coverage checks.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorStatusMessage>
          Active Fleet organization: <strong>{organizationLabel}</strong>.
        </OperatorStatusMessage>

        {sessionStatus === "loading" ? (
          <OperatorStatusMessage>Loading Fleet organization context…</OperatorStatusMessage>
        ) : null}
        {sessionError ? <OperatorStatusMessage tone="danger">{sessionError}</OperatorStatusMessage> : null}
        {error ? <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage> : null}
        {notice ? <OperatorStatusMessage>{notice}</OperatorStatusMessage> : null}

        {!organizationId ? (
          <OperatorPanel
            title="Organization context required"
            description="This workspace needs an organization scope from the current operator session or the latest Fleet onboarding persistence."
          >
            <OperatorEmptyState
              title="No Fleet organization is currently selected."
              message="Finish a Fleet onboarding review or open this workspace from an organization-bound operator session to manage persisted people."
            />
          </OperatorPanel>
        ) : (
          <>
            <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <OperatorMetricCard
                label="People records"
                value={people.length}
                detail="Persisted drivers, riders, helpers, conductors, and supervisors."
              />
              <OperatorMetricCard
                label="Active people"
                value={summary.activePeople}
                detail="People still marked active for assignment and issuance operations."
              />
              <OperatorMetricCard
                label="Assigned people"
                value={summary.assignedPeople}
                detail="People currently referenced by at least one persisted assignment."
              />
              <OperatorMetricCard
                label="Role coverage"
                value={summary.uniqueRoles}
                detail={`${summary.primaryRoles} records are primary-operator role types.`}
              />
            </section>

            <section className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <OperatorPanel
                title={editingPersonId ? "Edit person" : "Create person"}
                description="Maintain the persisted workforce roster independently from assets and onboarding drafts."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Full name</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.fullName}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, fullName: event.target.value }))
                      }
                      placeholder="Driver One"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Phone</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.phone}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, phone: event.target.value }))
                      }
                      placeholder="+919900001222"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Role type</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.roleType}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, roleType: event.target.value }))
                      }
                    >
                      <option value="driver">{primaryOperatorLabel}</option>
                      <option value="helper">{secondaryOperatorLabel}</option>
                      <option value="conductor">Conductor</option>
                      <option value="rider">Rider</option>
                      <option value="backup_rider">Backup rider</option>
                      <option value="supervisor">Supervisor</option>
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Operating unit</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.operatingUnitId}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          operatingUnitId: event.target.value,
                        }))
                      }
                    >
                      <option value="">Unassigned operating unit</option>
                      {unitOptions.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.code} • {unit.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm md:col-span-2">
                    <span className="text-muted-foreground">Status</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.status}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, status: event.target.value }))
                      }
                    >
                      <option value="active">Active</option>
                      <option value="reserve">Reserve</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </select>
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void savePerson()}
                    disabled={isSaving}
                    className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                  >
                    {isSaving ? "Saving…" : editingPersonId ? "Save person" : "Create person"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                  >
                    Clear form
                  </button>
                </div>
              </OperatorPanel>

              <OperatorPanel
                title="Persisted people roster"
                description="Review the persisted workforce, search by role or unit, and maintain operator coverage outside onboarding."
              >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <label className="min-w-[18rem] flex-1 text-sm">
                    <span className="mb-2 block text-muted-foreground">Search people</span>
                    <input
                      className="w-full rounded-2xl border border-border bg-background px-3 py-2"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search by name, role, phone, unit, or status"
                    />
                  </label>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3 text-sm text-muted-foreground">
                    Active assignments: <strong>{people.reduce((sum, person) => sum + person.assignmentCount, 0)}</strong>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-sm text-muted-foreground">Loading persisted people…</div>
                ) : filteredPeople.length === 0 ? (
                  <OperatorEmptyState
                    title="No people match the current view."
                    message="Create the first persisted person or broaden the search to see existing records."
                  />
                ) : (
                  <div className="grid gap-3">
                    {filteredPeople.map((person) => (
                      <div
                        key={person.id}
                        className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="text-lg font-semibold">{person.fullName}</div>
                            <div className="mt-1 text-sm text-muted-foreground">
                              {person.roleType} • {person.operatingUnit?.code || "No operating unit"}
                              {person.phone ? ` • ${person.phone}` : ""}
                            </div>
                            <div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                              {person.status}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => beginEditing(person)}
                              className="rounded-2xl border border-border px-3 py-2 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => void deletePerson(person)}
                              disabled={isDeletingPersonId === person.id}
                              className="rounded-2xl border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive disabled:opacity-60"
                            >
                              {isDeletingPersonId === person.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <OperatorMetricCard
                            label="Operating unit"
                            value={person.operatingUnit?.name || "Unassigned"}
                            detail={person.operatingUnit?.city || "No operating unit linked yet."}
                          />
                          <OperatorMetricCard
                            label="Assignments"
                            value={person.assignmentCount}
                            detail={`${person.primaryAssignmentCount} primary, ${person.secondaryAssignmentCount} secondary.`}
                          />
                          <OperatorMetricCard
                            label="Role type"
                            value={person.roleType}
                            detail={person.phone || "Phone not captured."}
                          />
                          <OperatorMetricCard
                            label="Updated"
                            value={formatFleetMasterTimestamp(person.updatedAt)}
                            detail={`Created ${formatFleetMasterTimestamp(person.createdAt)}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </OperatorPanel>
            </section>
          </>
        )}
      </div>
    </FleetB2BPageShell>
  )
}
